import React, { Reducer, useEffect, useRef, useReducer } from 'react';
import ReactDOM from 'react-dom';
import { ModalBase } from './modal.component';
import { ModalContent, ModalElement, ModalWrapper, Overlay, Grow } from './modal.styles';

type Action = { type: 'add'; modal: ModalBase } | { type: 'remove'; modalId: string };

type State = ModalBase[];

export let portalDispatch: React.Dispatch<Action> = null;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'add':
      return !state.map((item) => item.id).includes(action.modal.id) ? [...state, action.modal] : state;
    case 'remove':
      return state.filter((instance) => instance.id !== action.modalId);

    default:
      throw new Error();
  }
}

export interface PortalProps extends React.HTMLAttributes<HTMLDivElement> {
  portalId?: string;
}

const Portal: React.FC<PortalProps> = ({ portalId = 'modal-portal', ...props }) => {
  const [collection, dispatch] = useReducer<Reducer<ModalBase[], Action>>(reducer, []);

  const portalRef = useRef<HTMLDivElement>();

  useEffect(() => {
    const element = document.createElement('div');
    const portalElement = document.getElementById(portalId);
    if (portalElement) {
      portalElement.appendChild(element);
      portalRef.current = element;
    }

    return () => {
      element.remove();
    };
  }, []);

  useEffect(() => {
    portalDispatch = dispatch;
  }, [dispatch]);

  useEffect(() => {
    const activeModal = collection[collection.length - 1];
    if (!activeModal?.closeOnEscape) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        activeModal.close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [collection]);

  if (collection.length > 0) {
    const modalContent = (
      <ModalWrapper>
        {collection.map(({ id, renderModal, close, closeOnOverlayClick }, index) => (
          <ModalElement key={id} {...props} index={index}>
            <Overlay onClick={closeOnOverlayClick ? close : undefined} />
            <Grow in={true}>
              <ModalContent>{renderModal()}</ModalContent>
            </Grow>
          </ModalElement>
        ))}
      </ModalWrapper>
    );
    return portalRef?.current ? ReactDOM.createPortal(modalContent, portalRef.current) : modalContent;
  }

  return null;
};

export default Portal;
